import type { StateOrName } from "@uirouter/react";
import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";
import { ErrorBoundary } from "react-error-boundary";
import { useSelector } from "react-redux";

import { swSettings } from "app/common/services/swSettings";
import { EducationBarProminentProvider } from "app/components/educationbar/useEducationBarProminentContext";
import { ErrorFallback } from "app/components/error-boundary-no-data/ErrorBoundaryEmptyState";
import { PageNotificationBarContainer } from "app/components/React/NotificationBarContainer/PageNotificationBarContainer";
import { InfoBanner } from "app/components/React/PlaResearch/InfoBanner";
import { FeedbackSideTab } from "app/components/React/sideTabs";
import { RewardsSideBarContainer } from "app/components/React/sideTabs/RewardsSideBarContainer";
import useStickySubNav from "app/components/sub-navigation-bar/UseStickySubNav";
import { WebsiteAnalysisSubNav } from "app/components/sub-navigation-bar/WebsiteAnalysisSubNav";
import { SwDemoBanner } from "app/components/sw-demo-banner/SwDemoBanner";
import { WebsiteAnalysisProvider } from "app/pages/website-analysis/frame/WebsiteAnalysisContext";
import { WebsiteAnalysisPageFrame } from "app/pages/website-analysis/frame/WebsiteAnalysisPageFrame";
import { BetaBranchContainer } from "app/pages/website-analysis/TrafficAndEngagement/BetaBranch/BetaBranchContainer";
import { IS_BETA_BRANCH_ENABLED } from "app/pages/website-analysis/TrafficAndEngagement/BetaBranch/useBetaBranchPrefHook";
import { AlternativeSourcesNotificationBanner } from "app/pages/website-analysis/TrafficAndEngagement/Components/single/AlternativeSourcesNotificationBanner";
import type { ISite } from "pro-features/components/Workspace/Wizard/src/types";
import DurationService from "app/services/DurationService";
import { DEFAULT_PAGE_LAYOUT_CLASS_NAME } from "app/UtilitiesAndConstants/Constants/css";
import { InfoBannerContainer } from "app/UtilitiesAndConstants/UtilitiesComponents/InfoBannerContainer";

const contentContainerId = "websiteAnalysisContentContainerId";

interface IWebsiteAnalysisModuleFrameProps {
    shouldLogErrorFallback: boolean;
    filterRecentsBy?: StateOrName | StateOrName[];
    hideFavoriteButton?: boolean;
    suggestions?: ISite[];
    disableSubDomainsFilter?: boolean;
    removeSubDomainsFilter?: boolean;
    usePageLoader?: boolean;
    queryBarSearchHeader?: React.ReactNode;
    renderQueryBar?: () => React.ReactNode;
}

export const WebsiteAnalysisModuleFrame = ({
    shouldLogErrorFallback,
    filterRecentsBy,
    hideFavoriteButton = false,
    suggestions = null,
    disableSubDomainsFilter = false,
    removeSubDomainsFilter = false,
    usePageLoader,
    queryBarSearchHeader,
    renderQueryBar,
}: IWebsiteAnalysisModuleFrameProps) => {
    const { state, params } = useCurrentStateAndParams();
    const { name } = state;
    const { SubNavWrapper, PageContentWrapper, subNavWrapperProps, pageContentWrapperProps } = useStickySubNav();
    // if alternativeSource is true then we want to disable the page betta banner.
    const isAlternativeSource = useSelector((state: any) => {
        return state.common.alternativeSourceState.isAlternativeSource;
    });

    const isShowBetaBanner = () => {
        const durationData = DurationService.getDurationData(params.duration);
        return (
            IS_BETA_BRANCH_ENABLED() &&
            name === "digitalsuite_website_trafficandengagement" &&
            !(
                params.duration === "28d" ||
                params.comparedDuration ||
                durationData.raw.from.isBefore(
                    dayjs(swSettings.components["TrafficAndEngagementBeta"]?.resources?.StartDate),
                ) ||
                !swSettings.components["TrafficAndEngagementBeta"]?.resources?.AllowedCountries.includes(
                    parseInt(params.country, 10),
                )
            )
        );
    };

    const showBetaBannerFlag = isShowBetaBanner();
    const pageLayoutClassName = state.pageLayoutClassName ?? DEFAULT_PAGE_LAYOUT_CLASS_NAME;

    return (
        <ErrorBoundary
            FallbackComponent={(props) => (
                <ErrorFallback
                    {...props}
                    shouldLogErrorFallback={shouldLogErrorFallback}
                />
            )}
            resetKeys={[state, params]}
        >
            <EducationBarProminentProvider>
                <WebsiteAnalysisProvider>
                    <div className="sw-layout-module-inner">
                        <span
                            className="u-flex-row u-flex-column"
                            style={{ flexGrow: 1, overflowX: "hidden", width: "100%" }}
                        >
                            <SubNavWrapper
                                className="sw-layout-subnav hidden-print"
                                {...subNavWrapperProps}
                            >
                                <WebsiteAnalysisSubNav
                                    filterRecentsBy={filterRecentsBy}
                                    hideFavoriteButton={hideFavoriteButton}
                                    suggestions={suggestions}
                                    disableSubDomainsFilter={disableSubDomainsFilter}
                                    removeSubDomainsFilter={removeSubDomainsFilter}
                                    queryBarSearchHeader={queryBarSearchHeader}
                                    renderQueryBar={renderQueryBar}
                                />
                            </SubNavWrapper>
                            <SwDemoBanner type="basic" />
                            <InfoBanner
                                displayInTab="plaResearch"
                                userDataBannerKey="userDataBannerKeyWAPla"
                                text="keyword.analysis.plaResearch.page.toolTip.text"
                                title="keyword.analysis.plaResearch.page.toolTip.title"
                            />
                            <InfoBannerContainer
                                displayInPage={["organicsearch_websites_v2_traffic_keywords"]}
                                text="analysis.search.keywords.info.banner.text"
                                title="analysis.search.keywords.info.banner.title"
                                displayInDuration={["28d"]}
                                displayInWebSource={["MobileWeb"]}
                            />
                            {showBetaBannerFlag ? <BetaBranchContainer /> : null}
                            <PageNotificationBarContainer />
                            <PageContentWrapper
                                className="sw-layout-scrollable-element disable-top-padding sw-layout-research use-sticky-css-rendering"
                                {...pageContentWrapperProps}
                                id={contentContainerId}
                            >
                                <FeedbackSideTab />
                                <RewardsSideBarContainer />
                                {isAlternativeSource ? <AlternativeSourcesNotificationBanner /> : null}
                                <div className={`sw-page-websiteAnalysis with-top-padding ${pageLayoutClassName}`}>
                                    <section data-automation="page-frame-container">
                                        <WebsiteAnalysisPageFrame
                                            shouldLogErrorFallback={shouldLogErrorFallback}
                                            usePageLoader={usePageLoader}
                                        />
                                    </section>
                                </div>
                            </PageContentWrapper>
                        </span>
                    </div>
                </WebsiteAnalysisProvider>
            </EducationBarProminentProvider>
        </ErrorBoundary>
    );
};
